import { useState, useEffect } from "react";
import { PropertyCard } from "@/components/PropertyCard";
import { formatCurrency } from "@/utils/formatCurrency";
import type { PropertyFormData } from "@/components/form-property/property-types";

interface Property {
  id: number;
  attributes: PropertyFormData;
}

export function UpdateNotification() {
  const [outdatedProperties, setOutdatedProperties] = useState<Property[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const route = import.meta.env.VITE_URL_ENDPOINT;

  useEffect(() => {
    fetchOutdatedProperties();
  }, []);

  const fetchOutdatedProperties = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${route}/api/properties?populate=condominium,block,photos&pagination[pageSize]=100`,
      );
      const data = await response.json();
      const properties: Property[] = data.data || [];

      const now = new Date();
      const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);

      let filtered = properties.filter((property) => {
        const updatedAt = property.attributes.updatedAt
          ? new Date(property.attributes.updatedAt)
          : new Date(0);
        return updatedAt < ninetyDaysAgo;
      });
      setOutdatedProperties(filtered);
    } catch (error) {
      console.error("Erro ao buscar imóveis desatualizados:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCondoAttributes = (prop: any) => {
    const c = prop.condominium as unknown as {
      data?: { attributes?: any };
    };
    return c?.data?.attributes || {};
  };

  const getBlockAttributes = (prop: any) => {
    const b = prop.block as unknown as {
      data?: { attributes?: any };
    };
    return b?.data?.attributes || {};
  };

  const formatPropertyForCard = (property: Property) => {
    const attributes = property.attributes;
    const images = (
      (attributes.photos as { data: any[] } | undefined)?.data ?? []
    ).map((photoItem: any) => `${route}${photoItem.attributes.url}`);

    const capitalizeFirstLetter = (str: string) => {
      return str.charAt(0).toUpperCase() + str.slice(1);
    };

    let description = "";
    let propertyAttributes: any[] = [];

    if (attributes.propertyType === "apartment") {
      description = "Apartamento";
      propertyAttributes = [
        { label: "Quartos", value: attributes.numberOfBedrooms || "N/A" },
        { label: "Suítes", value: attributes.numberOfSuites || "N/A" },
        { label: "Vagas", value: attributes.garage || "N/A" },
        { label: "Área (m²)", value: attributes.usableArea || "N/A" },
      ];
    } else if (attributes.propertyType === "house") {
      description = "Casa";
      propertyAttributes = [
        { label: "Quartos", value: attributes.numberOfBedrooms || "N/A" },
        { label: "Suítes", value: attributes.numberOfSuites || "N/A" },
        { label: "Vagas", value: attributes.garage || "N/A" },
        { label: "Área (m²)", value: attributes.totalArea || "N/A" },
      ];
    } else if (attributes.propertyType === "area") {
      description = "Área";
      propertyAttributes = [
        { label: "Área Total (m²)", value: attributes.totalArea || "N/A" },
      ];
    } else if (attributes.propertyType === "commercial_property") {
      description = "Comercial";
      propertyAttributes = [
        { label: "Banheiros", value: attributes.bathrooms || "N/A" },
        { label: "Guarita", value: attributes.guardhouse || "N/A" },
        { label: "Salas", value: attributes.numberOfRooms || "N/A" },
        { label: "Área (m²)", value: attributes.totalArea || "N/A" },
      ];
    } else if (attributes.propertyType === "terrain") {
      description = "Terreno";
      propertyAttributes = [
        { label: "Área Total (m²)", value: attributes.totalArea || "N/A" },
      ];
    }

    let displayType = "";
    if (attributes.type && attributes.type.toLowerCase() !== "apartamento") {
      displayType = capitalizeFirstLetter(attributes.type);
    } else if (
      attributes.propertyType === "apartment" &&
      attributes.type &&
      attributes.type.toLowerCase() === "apartamento"
    ) {
      displayType = "";
    } else if (attributes.propertyType !== "apartment") {
      displayType = description;
    }

    const title = displayType
      ? `${attributes.neighborhood || ""} - ${displayType}`
      : `${attributes.neighborhood || ""} - ${description}`;
    const additionalInfo = `Região: ${attributes.region || ""}`;

    const condoName = getCondoAttributes(attributes).Name;
    const blockName = getBlockAttributes(attributes).name;

    const apNumber = attributes.apartmentNumber
      ? ` - ${attributes.apartmentNumber}`
      : "";

    let locationDescription = `${attributes.street || ""}, ${attributes.number || ""}${apNumber}`;

    if (condoName) {
      locationDescription += ` - ${condoName}`;
    }

    if (blockName && blockName !== condoName) {
      locationDescription += ` - ${blockName}`;
    }

    if (
      attributes.propertyType === "terrain" ||
      attributes.propertyType === "area"
    ) {
      const quadra = attributes.quadra
        ? `${attributes.street || ""}, ${attributes.number || ""} - Quadra ${attributes.quadra}`
        : "";
      const lote = attributes.lote ? `Lote(s) ${attributes.lote}` : "";
      locationDescription = [quadra, lote].filter(Boolean).join(" - ");
    }

    return {
      id: property.id,
      title,
      description: locationDescription,
      additionalInfo,
      status: attributes.status,
      images,
      price: attributes.price ? formatCurrency(attributes.price) : "N/A",
      condominiumPrice: attributes.condominiumFee
        ? formatCurrency(attributes.condominiumFee)
        : "N/A",
      tagKanlo: attributes.tagKanlo || "N/A",
      iptuPrice: attributes.iptu ? formatCurrency(attributes.iptu) : "N/A",
      attributes: propertyAttributes,
      updatedAt: attributes.updatedAt
        ? new Date(attributes.updatedAt).toISOString()
        : undefined,
      captador: attributes.captadores,
    };
  };

  if (outdatedProperties.length === 0) {
    return null;
  }

  return (
    <>
      {/* Botão de Notificação */}
      <button
        onClick={() => setShowModal(true)}
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-orange-500 text-white shadow-lg transition-all duration-300 hover:scale-110 hover:bg-orange-600"
        title="Imóveis precisando de atualização"
      >
        <i className="fas fa-sync-alt text-lg"></i>
        {outdatedProperties.length > 0 && (
          <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
            {outdatedProperties.length > 99 ? "99+" : outdatedProperties.length}
          </span>
        )}
      </button>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-xl bg-white shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 p-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Imóveis Precisando de Atualização
                </h2>
                <p className="text-sm text-gray-600">
                  {outdatedProperties.length} imóveis não foram atualizados há
                  mais de 90 dias
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>

            {/* Content */}
            <div className="max-h-[70vh] overflow-y-auto p-6">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="text-center">
                    <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-orange-200 border-t-orange-600"></div>
                    <p className="text-gray-600">Carregando imóveis...</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {outdatedProperties.map((property) => {
                    const cardProps = formatPropertyForCard(property);
                    return <PropertyCard key={property.id} {...cardProps} />;
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
